import React, { useCallback, useEffect, useState } from 'react';
import { Button, Drawer, List, Radio, Select, Space, Spin, Typography, message } from 'antd';
import { tripShareEn as t } from '../../content/tripShare.en';
import {
  addMember,
  listMembers,
  patchMemberRole,
  revokeMember,
  type ShareRole,
  type TripMembership,
  TripShareApiError,
} from '../../services/tripShareService';
import { listConnections } from '../../services/connectionsService';

type ConnectionRow = { connectedUserId: string; displayName?: string | null };

type Props = {
  open: boolean;
  tripId: string;
  onClose: () => void;
};

const TripShareRoster: React.FC<Props> = ({ open, tripId, onClose }) => {
  const [members, setMembers] = useState<TripMembership[]>([]);
  const [connections, setConnections] = useState<ConnectionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState<ShareRole>('viewer');
  const [selectedSub, setSelectedSub] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listMembers(tripId);
      setMembers(rows.filter((m) => m.status !== 'revoked'));
    } catch (e) {
      message.error(e instanceof Error ? e.message : t.retry);
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => {
    if (!open) return;
    void reload();
    void (async () => {
      try {
        const rows = await listConnections();
        setConnections(
          rows.map((r) => ({
            connectedUserId: r.connectedUserId,
            displayName: r.displayName ?? r.username ?? r.connectedUserId,
          }))
        );
      } catch {
        setConnections([]);
      }
    })();
  }, [open, reload]);

  const invite = async (): Promise<void> => {
    if (!selectedSub) return;
    setInviting(true);
    try {
      await addMember(tripId, selectedSub, role);
      message.success(t.inviteSent);
      setSelectedSub(null);
      await reload();
    } catch (e) {
      if (e instanceof TripShareApiError && e.status === 409) {
        message.error(t.memberCap);
      } else {
        message.error(e instanceof Error ? e.message : t.retry);
      }
    } finally {
      setInviting(false);
    }
  };

  const onRoleChange = async (member: TripMembership, next: ShareRole): Promise<void> => {
    try {
      await patchMemberRole(tripId, member.memberSub, next);
      await reload();
    } catch (e) {
      message.error(e instanceof Error ? e.message : t.retry);
    }
  };

  const onRevoke = async (member: TripMembership): Promise<void> => {
    try {
      await revokeMember(tripId, member.memberSub);
      await reload();
    } catch (e) {
      message.error(e instanceof Error ? e.message : t.retry);
    }
  };

  const memberSubs = new Set(members.map((m) => m.memberSub));
  const inviteOptions = connections
    .filter((c) => !memberSubs.has(c.connectedUserId))
    .map((c) => ({ value: c.connectedUserId, label: c.displayName || c.connectedUserId }));

  return (
    <Drawer
      title={t.peopleWithAccess}
      open={open}
      onClose={onClose}
      width={400}
      destroyOnClose
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Typography.Text type="secondary">{t.invite}</Typography.Text>
        <Select
          style={{ width: '100%' }}
          placeholder={t.invite}
          options={inviteOptions}
          value={selectedSub}
          onChange={setSelectedSub}
          showSearch
          optionFilterProp="label"
        />
        <Radio.Group value={role} onChange={(e) => setRole(e.target.value)}>
          <Radio.Button value="viewer">{t.viewer}</Radio.Button>
          <Radio.Button value="editor">{t.editor}</Radio.Button>
        </Radio.Group>
        <Button type="primary" onClick={() => void invite()} loading={inviting} disabled={!selectedSub}>
          {t.sendInvite}
        </Button>

        {loading ? (
          <Spin tip={t.loading} />
        ) : members.length === 0 ? (
          <Typography.Paragraph type="secondary">{t.emptyRoster}</Typography.Paragraph>
        ) : (
          <List
            header={t.members}
            dataSource={members}
            renderItem={(m) => (
              <List.Item
                actions={[
                  <Select
                    key="role"
                    size="small"
                    value={m.role === 'editor' ? 'editor' : 'viewer'}
                    style={{ width: 110 }}
                    onChange={(v) => void onRoleChange(m, v as ShareRole)}
                    options={[
                      { value: 'viewer', label: t.viewer },
                      { value: 'editor', label: t.editor },
                    ]}
                  />,
                  <Button key="rm" danger size="small" onClick={() => void onRevoke(m)}>
                    {t.remove}
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={m.memberSub.slice(0, 12) + (m.memberSub.length > 12 ? '…' : '')}
                  description={`${m.status === 'pending' ? t.pending : t.active} · ${m.role}`}
                />
              </List.Item>
            )}
          />
        )}
      </Space>
    </Drawer>
  );
};

export default TripShareRoster;
